import React, { useEffect, useState } from 'react';
import { FileUploader } from 'devextreme-react/file-uploader';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { BASE_URL_UPLOAD } from '../../api';
import { Consts } from '../../res';

const { PDF, MS_WORD, MS_EXCEL, MS_POWERPOINT, ZIP } = Consts.COURSE_RESOURCE_TYPES;

const TYPES_BY_EXTENSION = {
    pdf: PDF,
    doc: MS_WORD,
    docx: MS_WORD,
    xls: MS_EXCEL,
    xlsx: MS_EXCEL,
    ppt: MS_POWERPOINT,
    pptx: MS_POWERPOINT,
    zip: ZIP,
};

const ICONS = {
    [PDF]: 'file pdf outline',
    [MS_WORD]: 'file word outline',
    [MS_EXCEL]: 'file excel outline',
    [MS_POWERPOINT]: 'file powerpoint outline',
    [ZIP]: 'file archive outline',
};

const MAX_FILE_SIZE = 500 * 1024 * 1024;

const extensionOf = (name) => String(name || '').split('.').pop().toLowerCase();

/** The glyph for a resource's type — or, before it has one, for its file's extension. */
export const resourceIconOf = (type, url) => ICONS[type] || ICONS[TYPES_BY_EXTENSION[extensionOf(url)]] || 'file outline';

/** "…/api/fs/resource/1790500028974_guide.pdf" → "guide.pdf": the server puts a timestamp before a stored file's name. */
export const fileNameOf = (url) => {
    const last = String(url || '').split('/').pop();
    let name = last;
    try { name = decodeURIComponent(last); } catch { /* keep it as it came */ }
    return name.replace(/^\d+_/, '');
};

export const fileSizeText = (bytes) => {
    if (!bytes) return '';
    return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/*
 * A resource's file as a card: its type's glyph, its name (which opens it) and
 * its size, and — when `onRemove` is given — a way to take it out again.
 */
export const ResourceFile = ({ url, sizeInBytes, type, onRemove }) => {
    const { t } = useTranslation();
    if (!url) return null;

    return (
        <div className='tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-GREY_10 tw-bg-WHITE tw-px-3 tw-py-2'>
            <Icon name={resourceIconOf(type, url)} size='large' className='tw-text-PRIMARY_50' style={{ margin: 0 }} />
            <div className='tw-flex tw-min-w-0 tw-flex-1 tw-flex-col'>
                <a href={url} target='_blank' rel='noreferrer' title={t('OpenFile')} className='tw-truncate tw-font-bold tw-text-GREY_90 hover:tw-text-PRIMARY_50'>
                    {fileNameOf(url)}
                </a>
                {sizeInBytes ? <span dir='ltr' className='tw-self-start tw-text-xs tw-text-GREY_50'>{fileSizeText(sizeInBytes)}</span> : null}
            </div>
            {onRemove && (
                <button
                    type='button'
                    onClick={onRemove}
                    title={t('RemoveFile')}
                    aria-label={t('RemoveFile')}
                    className='tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-GREY_50 hover:tw-bg-ERROR_25 hover:tw-text-ERROR_100'
                >
                    <Icon name='close' style={{ margin: 0 }} />
                </button>
            )}
        </div>
    );
};

let nextZoneId = 0;

/*
 * One file of a course resource, uploaded to /resource/upload the moment it is
 * picked or dropped. The field is `resource` and the answer
 * `{ resourceUrl, sizeInBytes }`; the grid gets `{ url, sizeInBytes, type }`,
 * the type read off the extension, so a column's `setCellValue` can fill the
 * address, the size and — while it is still empty — the type in one go.
 *
 * The box is the drop zone and the button that opens the file picker; the
 * DevExtreme uploader behind it stays hidden. Under it, the uploaded file as a
 * card that opens it or takes it out again.
 */
const ResourceUploader = ({ data, sizeInBytes, type, hint }) => {
    const { t } = useTranslation();
    const [zoneId] = useState(() => `resource-drop-${++nextZoneId}`);
    const [uploading, setUploading] = useState(null); // { name, percent } while a file is on its way
    const [error, setError] = useState('');
    const [over, setOver] = useState(false);

    useEffect(() => {
        let open = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function () {
            open.apply(this, arguments);
            this.withCredentials = true;
        };
    }, []);

    // Checked here as well as by the uploader, which drops a wrong file without a word while it is hidden.
    const onValueChanged = (e) => {
        const file = e.value?.[0];
        if (!file) return;
        if (!TYPES_BY_EXTENSION[extensionOf(file.name)]) setError(t('ResourceBadType'));
        else if (file.size > MAX_FILE_SIZE) setError(t('ResourceTooLarge'));
        else setError('');
    };

    const onUploaded = (e) => {
        const { resourceUrl, sizeInBytes: size } = JSON.parse(e.request.response);
        setUploading(null);
        data.setValue({ url: resourceUrl, sizeInBytes: size, type: TYPES_BY_EXTENSION[extensionOf(e.file?.name)] ?? null });
    };

    const hasFile = Boolean(data.value);

    return (
        <div className='tw-flex tw-flex-col tw-gap-2'>
            <div
                id={zoneId}
                role='button'
                tabIndex={0}
                className={
                    'tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-xl ' +
                    'tw-border-2 tw-border-dashed tw-px-4 tw-text-center tw-transition-colors ' +
                    (hasFile && !uploading ? 'tw-py-3 ' : 'tw-py-6 ') +
                    (over ? 'tw-border-PRIMARY_50 tw-bg-PRIMARY_05' : 'tw-border-GREY_30 tw-bg-PRIMARY_2 hover:tw-border-PRIMARY_50')
                }
            >
                {uploading ? (
                    <div className='tw-flex tw-w-full tw-max-w-sm tw-flex-col tw-gap-2'>
                        <span className='tw-truncate tw-text-sm tw-font-bold tw-text-GREY_90'>{uploading.name}</span>
                        <div className='tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-GREY_10'>
                            <div className='tw-h-full tw-rounded-full tw-bg-PRIMARY_50 tw-transition-all' style={{ width: `${uploading.percent}%` }} />
                        </div>
                        <span className='tw-text-xs tw-text-GREY_70'>{t('ResourceUploading', { percent: uploading.percent })}</span>
                    </div>
                ) : hasFile ? (
                    <span className='tw-text-sm tw-text-PRIMARY_50'>
                        <Icon name='sync alternate' />
                        {t('ResourceReplaceFile')}
                    </span>
                ) : (
                    <>
                        <Icon name='cloud upload' size='big' className='tw-text-PRIMARY_50' style={{ margin: 0 }} />
                        <span className='tw-text-sm tw-font-bold tw-text-GREY_90'>{t('ResourceDropHere')}</span>
                        {hint && <span className='tw-text-xs tw-text-GREY_50'>{hint}</span>}
                    </>
                )}
            </div>

            {hasFile && !uploading && (
                <ResourceFile url={data.value} sizeInBytes={sizeInBytes} type={type} onRemove={() => data.setValue(null)} />
            )}

            {error && <span className='tw-text-xs tw-font-bold tw-text-ERROR_100'>{error}</span>}

            <FileUploader
                visible={false}
                multiple={false}
                accept='.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip'
                allowedFileExtensions={Object.keys(TYPES_BY_EXTENSION).map((extension) => `.${extension}`)}
                maxFileSize={MAX_FILE_SIZE}
                uploadMode='instantly'
                uploadUrl={`${BASE_URL_UPLOAD}/resource/upload`}
                name='resource'
                dialogTrigger={`#${zoneId}`}
                dropZone={`#${zoneId}`}
                onDropZoneEnter={() => setOver(true)}
                onDropZoneLeave={() => setOver(false)}
                onValueChanged={onValueChanged}
                onUploadStarted={(e) => { setOver(false); setError(''); setUploading({ name: e.file?.name, percent: 0 }); }}
                onProgress={(e) => setUploading((current) => current && { ...current, percent: e.bytesTotal ? Math.round((e.bytesLoaded / e.bytesTotal) * 100) : 0 })}
                onUploaded={onUploaded}
                onUploadError={() => { setUploading(null); setError(t('ResourceUploadFailed')); }}
                onUploadAborted={() => setUploading(null)}
            />
        </div>
    );
};

export default ResourceUploader;
